import type { PagedList } from './usePagedList'

/**
 * Find the best row in a list that is paged by a DIFFERENT order.
 *
 * ── WHY THIS IS NOT `items[0]` ──────────────────────────────────────────────
 *
 * `GET /users/{id}/recordings` pages newest-first by **`created_at`**
 * (`application/recordings.py`: the cursor is `(created_at, recording_id)`).
 * §1.1 asks for the most recent scored recording by its **actual recording
 * date**, which is `session_date`. For a window derived with
 * `scripts/derive_session_window.py`, a backfilled upload, or anything recorded
 * offline and sent later, those two orderings disagree — so today's result is
 * not necessarily on page one, and the newest row is not necessarily today's.
 *
 * A maximum over an unsorted key cannot stop early: the next page could always
 * hold a later date. So the scan is **bounded and says which bound it hit** —
 * the bound itself belongs to `usePagedList`'s `autoLoadPages`, and this is a
 * pure derivation over whatever that read.
 * `absent` and `capped` are different sentences on screen — §4.5's "distinguish
 * no match yet from an exhausted history" — and collapsing them would let a
 * partial scan claim there is nothing to show.
 */

export type PickState<T> =
  | { kind: 'scanning'; pagesScanned: number }
  /** Found, and the scan is finished or need not continue. */
  | { kind: 'picked'; item: T; pagesScanned: number; exhaustive: boolean }
  /** Every page was read and nothing matched. */
  | { kind: 'absent'; pagesScanned: number }
  /** The page budget ran out with pages still unread. NOT the same as absent. */
  | { kind: 'capped'; pagesScanned: number }

/** Five pages is 250 recordings at the default page size. */
export const DEFAULT_MAX_PAGES = 5

export function pickFromPages<T>(
  list: PagedList<T>,
  eligible: (item: T) => boolean,
  /** Standard comparator: negative when `a` sorts before `b`. Best is LAST. */
  better: (a: T, b: T) => number,
): PickState<T> {
  const { items, isLoading, isComplete, budgetSpent, pagesLoaded } = list

  if (isLoading) return { kind: 'scanning', pagesScanned: pagesLoaded }

  const matches = items.filter(eligible)
  const best = matches.length === 0 ? null : matches.reduce((a, b) => (better(a, b) < 0 ? b : a))

  if (best !== null) {
    // Reported even while more pages remain: it is the best SO FAR, and
    // `exhaustive` says whether a later page could still beat it. A screen that
    // showed it as final would be asserting something it has not read.
    return { kind: 'picked', item: best, pagesScanned: pagesLoaded, exhaustive: isComplete }
  }
  if (isComplete) return { kind: 'absent', pagesScanned: pagesLoaded }
  if (budgetSpent) return { kind: 'capped', pagesScanned: pagesLoaded }
  return { kind: 'scanning', pagesScanned: pagesLoaded }
}
